import { FC, useMemo, useEffect } from 'react';
import { FieldsSection, FormSelect } from '../../../../../../shared/components/form';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useLoaderData } from 'react-router-dom';
import { ITabState } from '../../../states/tabState.model.ts';
import { Stack, Typography } from '@mui/material';
import { notificationCenter } from '../../../../../../shared/notificationCenter/notificationCenter.ts';
import {
  printFormsService,
  SaveReportPrinterMappingsMutationRes,
} from '../../../services/printForms.service.ts';
import {
  C_Printed_Reports_Kind,
  C_Save_Operation_Status,
} from '../../../../../../graphql/generatedModel.ts';
import { take } from 'rxjs';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler.ts';
import { snackbarService } from '../../../../../../shared/components/snackbar/service/snackbar.service.ts';
import { DictCompanyPrintersQueryRes } from '../../../../../../shared/services/configsData/configsData.service.ts';

export const PrintersAssignment: FC<IPrintersAssignment> = () => {
  const { t } = useTranslation();
  const { companyPrinters, listReportPrinterMappings } = useLoaderData() as ITabState;
  const maxWidth = useMemo(() => {
    return (
      Math.max(
        ...listReportPrinterMappings.map(
          (item) => t(`settings.${item.printedReportsKindId}`).length,
        ),
      ) * 9
    );
  }, [t]);

  const formMethods = useForm({
    defaultValues: Object.fromEntries(
      listReportPrinterMappings.map(({ printedReportsKindId, printerId }) => [
        printedReportsKindId,
        printerId,
      ]),
    ),
  });

  useEffect(() => {
    const watchSub = formMethods.watch((fields, { type, name }) => {
      if (type === 'change') {
        notificationCenter.notify(printFormsService.sub.saveReportPrinterMappings(), {
          type: undefined,
          handler: (stream) => {
            stream.pipe(take(1), responseHandler()).subscribe((res) => {
              const typedRes = res as SaveReportPrinterMappingsMutationRes;
              if (typedRes?.status === C_Save_Operation_Status.SOS1_DATA_CHANGED) {
                snackbarService.pub.show({
                  type: 'success',
                  content: t('settings.configuration_saved'),
                });
              } else {
                snackbarService.pub.show({
                  type: 'error',
                  content: t('settings.configuration_not_saved'),
                });
              }
            });
          },
        });

        const printedReportsKindId = name as C_Printed_Reports_Kind;
        const printerId = fields[name as keyof typeof fields];
        printFormsService.pub.saveReportPrinterMappings({
          mapping: [{ printedReportsKindId, printerId }],
        });
        console.log(name, fields[name as keyof typeof fields]);
      }
    });
    return () => watchSub.unsubscribe();
  }, []);

  return (
    <FieldsSection titleBoxSx={{ pt: 0 }} nomt title={t('settings.form_printing_assignment')}>
      <FormProvider {...formMethods}>
        <Stack gap={3}>
          {listReportPrinterMappings.map(({ printedReportsKindId }, index) => (
            <Stack key={index} direction='row' gap={2} alignItems='center'>
              <Typography width={maxWidth}>{t(`settings.${printedReportsKindId}`)}</Typography>
              <FormSelect
                getOptionDisabled={(option) => {
                  const typedOption = option as unknown as DictCompanyPrintersQueryRes[number];
                  return typedOption.isDeactivated;
                }}
                width='350px'
                label={t('common.printer')}
                name={printedReportsKindId}
                options={companyPrinters as []}
              />
            </Stack>
          ))}
        </Stack>
      </FormProvider>
    </FieldsSection>
  );
};

interface IPrintersAssignment {}
